import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateOneTimeEntryInput,
  OneTimeEntry,
  OneTimeEntryCategory,
  UpdateOneTimeEntryInput,
} from "../../lib/realestate";
import { Modal } from "../ui/Modal";

const CATEGORIES: OneTimeEntryCategory[] = [
  "REPARATUR",
  "KAUTION",
  "NACHZAHLUNG",
  "SONDEREINNAHME",
  "SONDERAUSGABE",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateOneTimeEntryInput | UpdateOneTimeEntryInput) => Promise<void>;
  existing: OneTimeEntry | null;
  submitting: boolean;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

function absCentsToEuroDe(cents: number): string {
  return (Math.abs(cents) / 100).toFixed(2).replace(".", ",");
}

export function OneTimeEntryFormModal({ open, onClose, onSubmit, existing, submitting }: Props) {
  const { t } = useTranslation();
  const [date, setDate] = useState(existing?.date ?? todayIso());
  const [category, setCategory] = useState<OneTimeEntryCategory>(existing?.category ?? "REPARATUR");
  const [direction, setDirection] = useState<"income" | "expense">(
    existing ? (existing.amountCents >= 0 ? "income" : "expense") : "expense",
  );
  const [amount, setAmount] = useState(existing ? absCentsToEuroDe(existing.amountCents) : "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDate(existing?.date ?? todayIso());
    setCategory(existing?.category ?? "REPARATUR");
    setDirection(existing ? (existing.amountCents >= 0 ? "income" : "expense") : "expense");
    setAmount(existing ? absCentsToEuroDe(existing.amountCents) : "");
    setDescription(existing?.description ?? "");
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing]);

  const title = existing
    ? t("realestate.one_time.form_title_edit")
    : t("realestate.one_time.add_title");

  async function handleSubmit() {
    setError(null);
    try {
      const absCents = parseEuroDe(amount);
      const amountCents = direction === "income" ? absCents : -absCents;
      const trimmedNotes = notes.trim();
      const input: CreateOneTimeEntryInput | UpdateOneTimeEntryInput = {
        date,
        amountCents,
        category,
        description: description.trim(),
        notes: trimmedNotes === "" ? null : trimmedNotes,
      };
      await onSubmit(input);
      if (!existing) {
        setAmount("");
        setDescription("");
        setNotes("");
      }
      onClose();
    } catch {
      setError(t("realestate.errors.invalid_amount"));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("realestate.one_time.date")}</span>
          <input
            type="date"
            max={todayIso()}
            className="block mt-1 w-full border rounded px-2 py-1"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.one_time.category")}</span>
          <select
            className="block mt-1 w-full border rounded px-2 py-1"
            value={category}
            onChange={(e) => setCategory(e.target.value as OneTimeEntryCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`realestate.one_time.category_label.${c}`)}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="flex gap-3">
          <label className="text-sm">
            <input
              type="radio"
              name="direction"
              checked={direction === "expense"}
              onChange={() => setDirection("expense")}
              className="mr-1"
            />
            {t("realestate.one_time.expense")}
          </label>
          <label className="text-sm">
            <input
              type="radio"
              name="direction"
              checked={direction === "income"}
              onChange={() => setDirection("income")}
              className="mr-1"
            />
            {t("realestate.one_time.income")}
          </label>
        </fieldset>
        <label className="block">
          <span className="text-sm">{t("realestate.one_time.amount")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1.500,00"
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.one_time.description")}</span>
          <input
            className="block mt-1 w-full border rounded px-2 py-1"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            maxLength={160}
            placeholder="z.B. Boiler-Reparatur"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.one_time.notes")}</span>
          <textarea
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={2}
          />
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
